import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Handles the "Add gift" action for a completed free-gift reward tier.
 *
 * Cart state updates ride the theme's existing CartLinesUpdateEvent flow:
 * dispatching it lets `cart-items-component` (an ancestor) morph its own
 * section from the `sections` payload, and lets `cart-icon.js` (elsewhere
 * on the page) refresh the header bubble count - this component does not
 * morph anything itself.
 *
 * There is no cross-request abort here: a single `<cart-rewards-component>`
 * can render more than one gift button (one per completed gift tier), and
 * unlike a discount-code list update, one gift add does not subsume
 * another - aborting button A's in-flight, non-idempotent `/cart/add.js`
 * request because button B was clicked would risk losing or duplicating
 * an add. Reentrancy on the *same* button is already prevented by
 * disabling it for the duration of its own request.
 *
 * @typedef {Object} CartRewardsRefs
 * @property {HTMLElement} giftError - The shared gift-add error message element.
 *
 * @extends {Component<CartRewardsRefs>}
 */
class CartRewards extends Component {
  requiredRefs = ['giftError'];

  /**
   * Tier info cards are plain elements toggled via the `hidden` attribute -
   * not the native Popover API. That was the previous approach
   * (popover="auto", then "manual") and both turned out unreliable here:
   * this component always renders inside the cart drawer's <dialog>, and
   * a popover that's a descendant of an open modal dialog has a
   * documented top-layer interaction where the browser's own open/close
   * handling races - not something fixable by CSS or listener tweaks on
   * our side. Owning open/close state directly sidesteps the browser
   * behavior entirely instead of continuing to work around it.
   *
   * Opening/closing routes through the theme's own declarative on:click /
   * on:keydown system (see component.js) rather than manual
   * addEventListener calls on specific nodes. That system dispatches from
   * a single document-level, capture-phase listener registered once for
   * the whole page and resolved fresh via `closest('[on:click]')` on
   * every event - so it keeps working correctly even if the cart drawer's
   * morph/AJAX re-render fully replaces these buttons and cards with new
   * DOM nodes, with no re-initialization and no risk of duplicate
   * listeners. Only "click outside to close" needs a real
   * document.addEventListener, since that has to fire on clicks that
   * don't target this component at all.
   */

  /**
   * Opens the clicked tier's card, closing any other open one first. If
   * that tier's card is already open, closes it instead (second click on
   * the same icon toggles closed).
   * @param {{tier: number}} data - Parsed from the button's on:click="/toggleTierCard?tier=N".
   * @param {MouseEvent} event
   */
  toggleTierCard = (data, event) => {
    const trigger = event.target instanceof Element ? event.target.closest('.cart-rewards__marker-button') : null;
    if (!(trigger instanceof HTMLElement)) return;

    const card = this.#cardFor(trigger);
    if (!card) return;

    if (!card.hidden) {
      this.#closeCard(card, trigger);
      return;
    }

    this.#closeOpenCard();
    this.#openCard(card, trigger);
  };

  /**
   * Closes the tier card containing the clicked close (×) button.
   * @param {MouseEvent} event
   */
  closeTierCard = (event) => {
    const card = event.target instanceof Element ? event.target.closest('.cart-rewards__tier-card') : null;
    if (!(card instanceof HTMLElement)) return;
    this.#closeCard(card, this.#triggerFor(card));
  };

  /**
   * Closes the open tier card on Escape, without also closing the cart
   * drawer behind it. Bound declaratively via on:keydown on this
   * component's root, so it fires regardless of whether focus is on a
   * trigger button or inside the open card. theme-drawer.js's own Escape
   * handler already checks `event.defaultPrevented` before closing the
   * drawer ("let nested components handle Escape first") - the
   * declarative system's document-level listener runs in capture phase
   * (see component.js), which always fires before theme-drawer's plain
   * bubble-phase listener, so preventDefault() here reliably reaches it
   * in time regardless of connectedCallback ordering between the two.
   * @param {KeyboardEvent} event
   */
  handleTierCardKeydown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const card = this.#findOpenCard();
    if (!card) return;

    event.preventDefault();
    this.#closeCard(card, this.#triggerFor(card));
  };

  /**
   * Closes the open tier card when the user clicks/taps outside it and
   * outside its own trigger button. Capture phase so this reliably runs
   * ahead of other click handling (e.g. the drawer's own backdrop-close).
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const card = this.#findOpenCard();
    if (!card || !(event.target instanceof Node)) return;

    const trigger = this.#triggerFor(card);
    if (card.contains(event.target) || trigger?.contains(event.target)) return;

    this.#closeCard(card, trigger);
  };

  /** @returns {HTMLElement | null} The currently open tier card, if any. */
  #findOpenCard() {
    return this.querySelector('.cart-rewards__tier-card:not([hidden])');
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {HTMLElement | null}
   */
  #cardFor(trigger) {
    const cardId = trigger.getAttribute('aria-controls');
    const card = cardId ? this.querySelector(`#${CSS.escape(cardId)}`) : null;
    return card instanceof HTMLElement ? card : null;
  }

  /**
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  #triggerFor(card) {
    const trigger = this.querySelector(`[aria-controls="${CSS.escape(card.id)}"]`);
    return trigger instanceof HTMLElement ? trigger : null;
  }

  #closeOpenCard() {
    const card = this.#findOpenCard();
    if (card) this.#closeCard(card, this.#triggerFor(card));
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement} trigger
   */
  #openCard(card, trigger) {
    card.hidden = false;
    this.#positionCard(card, trigger);
    // Force a layout flush so the browser registers "just unhidden" as a
    // real starting frame before the opening class applies - without this
    // the opacity/translate transition can be skipped entirely on some
    // browsers, which is exactly what read as a "flicker" before.
    card.getBoundingClientRect();
    card.classList.add('cart-rewards__tier-card--open');
    trigger.setAttribute('aria-expanded', 'true');
    // Clears the unread-style notification dot the first time this tier's
    // card is actually opened - see .cart-rewards__marker-badge.
    trigger.classList.add('cart-rewards__marker-button--viewed');
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement | null} trigger
   */
  #closeCard(card, trigger) {
    card.classList.remove('cart-rewards__tier-card--open');
    trigger?.setAttribute('aria-expanded', 'false');
    // onAnimationEnd resolves immediately (no hang) when there's no active
    // transition - e.g. cart_rewards_animations_enabled is off - so this
    // is never an arbitrary wait, just "after whatever is actually
    // playing finishes, if anything is."
    onAnimationEnd(card, () => {
      if (!card.classList.contains('cart-rewards__tier-card--open')) card.hidden = true;
    });
  }

  /**
   * Anchors the card under its own trigger, clamped so it can't overflow
   * the drawer. This has to be computed in JS rather than pure CSS
   * because the safe clamp range depends on the card's actual rendered
   * width, which varies with merchant-entered text length per tier.
   * Written in inline-start-relative terms throughout (not "left"), so
   * it's correct in RTL without a separate physical-property flip.
   * @param {HTMLElement} card
   * @param {HTMLElement} trigger
   */
  #positionCard(card, trigger) {
    const container = this.querySelector('.cart-rewards__progress');
    if (!(container instanceof HTMLElement)) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const containerRect = container.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const margin = 8;

    const triggerCenterInlineStart = isRtl
      ? containerRect.right - (triggerRect.left + triggerRect.width / 2)
      : triggerRect.left + triggerRect.width / 2 - containerRect.left;

    const maxInlineStart = Math.max(containerRect.width - cardRect.width - margin, margin);
    const inlineStart = Math.min(Math.max(triggerCenterInlineStart - cardRect.width / 2, margin), maxInlineStart);

    card.style.setProperty('--cart-rewards-card-offset', `${inlineStart}px`);
    card.style.setProperty('--cart-rewards-card-pointer-position', `${triggerCenterInlineStart - inlineStart}px`);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick, { capture: true });
  }

  /**
   * Adds the configured gift product/variant to the cart.
   * @param {MouseEvent} event - The click event on the "Add gift" button.
   */
  addGift = async (event) => {
    event.preventDefault();

    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const { variantId, tierIndex } = button.dataset;
    if (!variantId || !tierIndex) return;

    const cartItemsComponent = this.closest('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement) || !cartItemsComponent.dataset.sectionId) return;

    const sectionId = cartItemsComponent.dataset.sectionId;
    const { giftError } = this.refs;

    button.disabled = true;
    giftError.classList.add('hidden');

    const deferredPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-rewards',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _reward_tier_gift: tierIndex },
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        const message =
          typeof data.description === 'string'
            ? data.description
            : typeof data.message === 'string'
              ? data.message
              : this.dataset.genericGiftError;
        giftError.textContent = message;
        giftError.classList.remove('hidden');
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferredPromise.reject(new Error(message));
        button.disabled = false;
        return;
      }

      // The add already succeeded server-side at this point. The header
      // bubble's count is already in this response's own section HTML (the
      // same `[ref="cartItemCount"]` element component-cart-items.js reads
      // from a single response) - read it from there first, so the section
      // morph and the count update both resolve without waiting on a second
      // /cart.json round-trip. Only fall back to a full refresh (with a null
      // cart if that also fails - it must not be reported as an add failure,
      // which would leave the button re-enabled and invite a second, paid
      // add) when the count isn't readable from the section HTML.
      const countText = data.sections?.[sectionId]
        ? new DOMParser().parseFromString(data.sections[sectionId], 'text/html').querySelector('[ref="cartItemCount"]')
            ?.textContent
        : undefined;
      const fastItemCount = countText ? parseInt(countText, 10) : undefined;

      if (fastItemCount !== undefined) {
        deferredPromise.resolve({
          cart: {
            id: '',
            totalQuantity: fastItemCount,
            cost: { totalAmount: { amount: '', currencyCode: '' } },
            lines: [],
            discountCodes: [],
          },
          detail: { sections: data.sections },
        });
      } else {
        /** @type {import('@theme/component-cart-items').Cart | null} */
        let freshCart = null;
        try {
          freshCart = await /** @type {import('@theme/component-cart-items').CartItemsComponent} */ (
            cartItemsComponent
          ).fetchCartData();
        } catch (fetchCartError) {
          console.warn('[cart-rewards] Failed to refresh cart after adding gift', fetchCartError);
        }

        deferredPromise.resolve({
          cart: freshCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart) : null,
          detail: { sections: data.sections },
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : this.dataset.genericGiftError;
      giftError.textContent = message;
      giftError.classList.remove('hidden');
      deferredPromise.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      button.disabled = false;
    }
  };
}

if (!customElements.get('cart-rewards-component')) {
  customElements.define('cart-rewards-component', CartRewards);
}
